import React, { useState, useEffect } from 'react';
import { authService } from '../../../services/api';

const EmailVerification = ({ onNavigate, email, token: propToken, status: propStatus }) => {
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get token and status from props or URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = propToken || urlParams.get('token');
    const urlStatus = propStatus || urlParams.get('status');
    
    if (urlToken) {
      // If status is already set from backend redirect, use it
      if (urlStatus === 'success') {
        setVerificationStatus('success');
        setLoading(false);
      } else if (urlStatus === 'expired' || urlStatus === 'error') {
        setVerificationStatus('error');
        setError(urlStatus === 'expired' ? 'This verification link has expired. Please request a new one.' : 'Invalid verification link. Please try again.');
        setLoading(false);
      } else {
        // No status, verify via API
        verifyEmail(urlToken);
      }
    }
  }, [propToken, propStatus]);

  const verifyEmail = async (token) => {
    setLoading(true);
    setError('');
    
    try {
      await authService.verifyEmail(token);
      setVerificationStatus('success');
    } catch (error) {
      setError(error.message);
      setVerificationStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Note: This would require a resend verification endpoint
      // For now, we'll just show a message
      setError('Please check your email for the verification link. If you need a new one, please contact support.');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (verificationStatus === 'success') {
    return (
      <div className="text-center space-y-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Email Verified!</h3>
          <p className="text-gray-600">
            Your email has been successfully verified. You can now log in to your account.
          </p>
        </div>
        
        <button
          type="button"
          onClick={() => onNavigate('login')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
        >
          Log in
        </button>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="text-center space-y-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Verification Failed</h3>
          <p className="text-gray-600 mb-2">
            {error || 'There was an issue verifying your email. Please try again.'}
          </p>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => setVerificationStatus('pending')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            Try Again
          </button>
          
          <button
            onClick={() => onNavigate('login')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-medium transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Verify your email</h3>
        <p className="text-gray-600">
          We've sent a verification link to <strong>{email}</strong>
        </p>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-left">
        <h4 className="text-sm font-medium text-blue-800 mb-2">What to do next:</h4>
        <ol className="text-sm text-blue-700 space-y-1">
          <li>1. Check your email inbox</li>
          <li>2. Click the verification link in the email</li>
          <li>3. Return here to complete the process</li>
        </ol>
      </div>
      
      <div className="text-sm text-gray-500">
        <p>Didn't receive the email? Check your spam folder or</p>
        <button
          onClick={resendVerification}
          disabled={loading}
          className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'resend verification email'}
        </button>
      </div>
      
      <div className="pt-4">
        <button
          onClick={() => onNavigate('login')}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          ← Back to login
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;
